import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { httpsCallable } from "firebase/functions";
import { Plugs, WarningCircle } from "@phosphor-icons/react";
import { functions } from "../../firebase";
import {
  selectClinicId,
  selectClinicProfile,
  setClinicProfile,
} from "../../store/slices/authSlice";
import { useToast } from "../../contexts/ToastContext";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./MorningSettings.css";

/**
 * MorningDisconnectModal — confirm + run `disconnectMorning`.
 * Documents already issued stay in Morning and in the local cache.
 * Error code: CK-8816
 */
export default function MorningDisconnectModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const confirm = async () => {
    setIsLoading(true);
    try {
      const disconnectMorning = httpsCallable(functions, "disconnectMorning");
      const res = await disconnectMorning({ clinicId });
      if (!res.data?.success) throw new Error(res.data?.error || "הניתוק נכשל");
      dispatch(
        setClinicProfile({
          ...clinicProfile,
          isMorningPaymentSystemIntegrated: false,
          morningIntegration: null,
        })
      );
      showSuccess("מורנינג נותק מהקליניקה");
      onClose();
    } catch (err) {
      console.error("[CK-8816] Morning disconnect failed", {
        errorCode: "CK-8816",
        errorMessage: err.message,
        timestamp: new Date().toISOString(),
      });
      showError(err.message || "שגיאה בניתוק מורנינג");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mset-overlay" onClick={onClose} role="presentation">
      <div
        className="mset-modal mset-modal-sm"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mset-disc-title"
      >
        <div className="mset-header">
          <div className="mset-title">
            <span className="mset-icon danger">
              <Plugs size={20} weight="fill" />
            </span>
            <div>
              <span className="mset-eyebrow">מורנינג</span>
              <h3 id="mset-disc-title">ניתוק החיבור למורנינג</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="mset-body">
          <p>
            לאחר הניתוק לא ניתן יהיה להפיק חשבוניות וקבלות דרך מורנינג מתוך
            CliniKit. מסמכים שכבר הופקו נשארים במורנינג ובהיסטוריה כאן.
          </p>
          <div className="mset-callout warn">
            <WarningCircle size={18} weight="fill" />
            <span>מפתח ה-API יימחק מהמערכת. להתחברות מחדש יהיה צורך להזין אותו שוב.</span>
          </div>
        </div>

        <div className="mset-footer">
          <button type="button" className="mset-btn secondary" onClick={onClose} disabled={isLoading}>
            ביטול
          </button>
          <button type="button" className="mset-btn danger" onClick={confirm} disabled={isLoading}>
            {isLoading ? <LoaderLoop size="xs" /> : <Plugs size={16} />}
            ניתוק ממורנינג
          </button>
        </div>
      </div>
    </div>
  );
}
